/**
 * Shared React Query defaults — reduce refetches and align keys across the app.
 */

import type { QueryClient } from '@tanstack/react-query';
import { clearCachedApiResponseByPrefix } from '../utils/cache';
import { invalidateEntityCache } from '../utils/cacheInvalidation';
import { bumpTransactionsListCacheGeneration } from '../services/transactionsService';

/** Stale times (ms) */
export const STALE_TIME_MS = {
  /** Dashboard split endpoints (overview / monthly / recent) — 3–5 min band */
  dashboard: 4 * 60 * 1000,
  dashboardGst: 4 * 60 * 1000,
  /** Advanced analytics */
  analyticsSummary: 8 * 60 * 1000,
  analyticsFull: 5 * 60 * 1000,
  analyticsTab: 5 * 60 * 1000,
  /** Transaction list */
  transactionsList: 2 * 60 * 1000,
  /** Detail panels */
  transactionDetail: 5 * 60 * 1000,
  notificationDetail: 5 * 60 * 1000,
  reportDetail: 5 * 60 * 1000,
  notificationsList: 2 * 60 * 1000,
  /** Receivables & payables lists */
  arApList: 2 * 60 * 1000,
  arApDetail: 5 * 60 * 1000,
  arApSummary: 4 * 60 * 1000,
} as const;

export const defaultRefetchPolicy = {
  refetchOnWindowFocus: false as const,
  refetchOnMount: false as const,
};

/** AR/AP lists and contact pickers — avoid stale dropdowns after create. */
export const arApFreshDataPolicy = {
  staleTime: 0,
  refetchOnMount: 'always' as const,
  refetchOnWindowFocus: false as const,
};

/** Serializable slice used in React Query keys for GET /transactions */
export type TransactionListFiltersKey = {
  limit: number;
  type: string;
  category: string;
  paymentMethod: string;
  startDate: string;
  endDate: string;
  month: number;
  year: number;
  search: string;
};

export type TransactionTotalsFiltersKey = {
  month: number;
  year: number;
  startDate: string;
  endDate: string;
  type: string;
  category: string;
  paymentMethod: string;
  search: string;
};

export type NotificationListFiltersKey = {
  limit: number;
  filter: 'all' | 'read' | 'unread';
};

export type ArApListFiltersKey = {
  kind: 'receivable' | 'payable';
  statusCardFilter: 'open' | 'overdue' | 'settled' | 'all' | null;
  category: string;
  search: string;
  contactId: string;
  selectedYear: number;
  selectedMonth: number;
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
};

/**
 * Query keys — prefix + scoped args so lists vs details never collide.
 * Note: dashboard overview still includes `year` because the API sends `?year=` (see dashboardService.getOverview).
 */
export const queryKeys = {
  dashboardOverview: (orgId: string | null, year: number) =>
    ['dashboard-overview', orgId, year] as const,
  dashboardMonthly: (orgId: string | null, year: number) =>
    ['dashboard-monthly', orgId, year] as const,
  dashboardRecent: (orgId: string | null) => ['dashboard-recent', orgId] as const,
  dashboardGst: (orgId: string | null, year: number) =>
    ['dashboard-gst', orgId, year] as const,

  analyticsSummary: (year: number) => ['analytics-summary', year] as const,
  analyticsFull: (year: number) => ['analytics-full', year] as const,

  transactionsList: (orgId: string | null, page: number, filters: TransactionListFiltersKey) =>
    ['transactions', orgId, page, filters] as const,
  transactionsTotals: (orgId: string | null, filters: TransactionTotalsFiltersKey) =>
    ['transactions-totals', orgId, filters] as const,

  notificationsList: (orgId: string | null, page: number, filters: NotificationListFiltersKey) =>
    ['notifications', orgId, page, filters] as const,
  caNotificationsList: (page: number, limit: number) =>
    ['ca-notifications', page, limit] as const,

  receivablesList: (orgId: string | null, filters: ArApListFiltersKey) =>
    ['receivables', orgId, filters] as const,
  payablesList: (orgId: string | null, filters: ArApListFiltersKey) =>
    ['payables', orgId, filters] as const,
  receivablesSummary: (orgId: string | null) => ['receivables-summary', orgId] as const,
  payablesSummary: (orgId: string | null) => ['payables-summary', orgId] as const,
  arApKpiSnapshot: (orgId: string | null) => ['ar-ap-kpi-snapshot', orgId] as const,
  arApCurrentBalance: (orgId: string | null) => ['ar-ap-current-balance', orgId] as const,
  arApCurrentBalanceLogs: (orgId: string | null) => ['ar-ap-current-balance-logs', orgId] as const,
  arApPickerContacts: (orgId: string | null, type: 'client' | 'vendor') =>
    ['ar-ap-contacts-picker', orgId, type] as const,
  receivableDetail: (orgId: string | null, id: string) =>
    ['receivable-detail', orgId, id] as const,
  payableDetail: (orgId: string | null, id: string) =>
    ['payable-detail', orgId, id] as const,
} as const;

/** Partial invalidation: all list pages + detail caches for transactions */
export function invalidateTransactionsTree(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['transactions'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['transactions-totals'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['transaction-detail'], exact: false });
}

/** Clear localStorage transaction cache and invalidate React Query (use after AR/AP settlement). */
export function refreshTransactionsTree(queryClient: QueryClient): void {
  bumpTransactionsListCacheGeneration();
  clearCachedApiResponseByPrefix('/transactions');
  invalidateEntityCache('transactions');
  invalidateTransactionsTree(queryClient);
}

/** Partial invalidation: counts, all notification lists, all detail panels */
export function invalidateNotificationsTree(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
  void queryClient.invalidateQueries({ queryKey: ['notifications'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ca-notifications'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['notification-detail'], exact: false });
}

/** Cashflow charts, pending AR/AP movements, and opening/current balance used on Cashflow */
export function invalidateCashflowTree(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['cashflow'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['cashflow-pending-ar-ap'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ar-ap-current-balance'], exact: false });
}

/** Receivables, payables, summaries, and dashboard KPIs after AR/AP mutations */
export function invalidateArApTree(
  queryClient: QueryClient,
  options?: { refreshTransactions?: boolean }
): void {
  void queryClient.invalidateQueries({ queryKey: ['receivables'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['payables'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['receivables-summary'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['payables-summary'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ar-ap-kpi-snapshot'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ar-ap-current-balance'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ar-ap-current-balance-logs'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['ar-ap-contacts-picker'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['contacts-picker'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['receivable-detail'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['payable-detail'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['commentCounts'], exact: false });
  invalidateCashflowTree(queryClient);
  if (options?.refreshTransactions) {
    invalidateTransactionsTree(queryClient);
  }
  void queryClient.invalidateQueries({ queryKey: ['dashboard-overview'], exact: false });
}

